import type { Location } from 'react-router-dom';
import type { WizardFormValues } from '../../../../domain/customerInquiry';
import { contactPageRoutePrefix } from '../../../../MainRouter/router.constants';
import StepGenericTile from '../steps/StepGenericTile';
import { WIZARD_STRUCTURE } from '../config/wizardStructure.config';
import type { WizardNode } from '../types/wizardTypes';
import { coerceToString } from '../../../../utils/coerce';
import { getWizardChildNode, getWizardTileConfigs } from './wizardNodeUtils';

function getPathAndFormValueFromWizardNodeAndUrl(
  pathUrlComponent: string,
  wizardNode: WizardNode
) {
  let pathValue: number | null = null;
  let formValue: Record<string, string> | null = null;
  const { componentClass } = wizardNode;
  if (componentClass === StepGenericTile) {
    const tileConfigs = getWizardTileConfigs(wizardNode);
    const { fieldName } = wizardNode;
    const tileOption = tileConfigs?.find(
      (tc) => tc.urlValue === pathUrlComponent
    );
    if (tileOption && fieldName) {
      formValue = {
        [fieldName]: tileOption.value,
      };
      if (wizardNode.noMapNeeded) {
        pathValue = 0;
      } else if (wizardNode.map) {
        pathValue = wizardNode.map[tileOption.value];
      }
    }
  } else {
    const { nodes } = wizardNode;
    pathValue = nodes?.findIndex((n) => n.urlValue === pathUrlComponent) ?? -1;
    if (pathValue < 0) {
      pathValue = null;
    }
  }
  return { pathValue, formValue };
}

export function getFormStepAndPathFromLocation(
  location: Location,
  initialValues = false
): [Partial<WizardFormValues>, number, number[]] {
  let stepFromLocation = 0;
  let pathFromLocation: number[] = [];
  let formValues: Partial<WizardFormValues> = {};

  const { pathname } = location;
  if (pathname.startsWith(contactPageRoutePrefix)) {
    let pathWithoutPrefix = pathname.slice(contactPageRoutePrefix.length);
    if (pathWithoutPrefix.startsWith('/')) {
      pathWithoutPrefix = pathWithoutPrefix.slice(1);
    }
    const pathUrlComponents = pathWithoutPrefix.split('/');

    let currentNode = WIZARD_STRUCTURE;
    for (let i = 0; i < pathUrlComponents.length; i += 1) {
      const pathUrlComponent = pathUrlComponents[i];
      const { pathValue, formValue } = getPathAndFormValueFromWizardNodeAndUrl(
        pathUrlComponent,
        currentNode
      );
      if (pathValue == null) {
        break;
      }
      pathFromLocation.push(pathValue);
      stepFromLocation += 1;
      const childNode = getWizardChildNode(currentNode, pathValue);
      if (!childNode) {
        break;
      }
      currentNode = childNode;

      if (currentNode.skipThisStep) {
        stepFromLocation += 1;
        pathFromLocation.push(0);
        const skipChild = currentNode.nodes?.[0];
        if (!skipChild) {
          break;
        }
        currentNode = skipChild;
      }

      if (formValue !== null) {
        formValues = {
          ...formValues,
          ...formValue,
        };
      }
    }
  }

  if (initialValues) {
    if (stepFromLocation > 3) {
      stepFromLocation = 3;
      pathFromLocation = pathFromLocation.slice(0, 3);
    }
  }

  return [formValues, stepFromLocation, pathFromLocation];
}

export function getNewLocationFromState(
  currentStep: number,
  currentPath: number[],
  formValues: WizardFormValues
) {
  const urlComponents: string[] = [];
  let currentNode = WIZARD_STRUCTURE;
  for (let i = 0; i < currentStep; i += 1) {
    const { componentClass } = currentNode;
    if (!currentNode.skipThisStep) {
      if (componentClass === StepGenericTile) {
        const tileConfigs = getWizardTileConfigs(currentNode);
        const { fieldName } = currentNode;
        const fieldValue = fieldName ? formValues[fieldName] : undefined;
        const tileOption = tileConfigs?.find((tc) => tc.value === fieldValue);
        if (tileOption) {
          urlComponents.push(tileOption.urlValue);
        } else {
          break;
        }
      } else {
        const { nodes } = currentNode;
        if (Array.isArray(nodes) && nodes.length) {
          const urlValue = nodes[0].urlValue;
          if (urlValue) {
            urlComponents.push(urlValue);
          }
        } else {
          break;
        }
      }
    }
    const childNode = getWizardChildNode(currentNode, currentPath[i]);
    if (!childNode) {
      break;
    }
    currentNode = childNode;
  }

  let urlPrefix = contactPageRoutePrefix;
  if (urlComponents.length === 0) {
    return urlPrefix;
  }
  if (!urlPrefix.endsWith('/')) {
    urlPrefix = `${urlPrefix}/`;
  }
  return `${urlPrefix}${urlComponents.join('/')}`;
}

export function getNextOrBackFromLocationDiff(
  stateLocation: string,
  pathname: string
): 'back' | 'next' | null {
  if (
    !stateLocation.startsWith(contactPageRoutePrefix) ||
    !pathname.startsWith(contactPageRoutePrefix)
  ) {
    return null;
  }

  const current = stateLocation.slice(contactPageRoutePrefix.length);
  const pending = pathname.slice(contactPageRoutePrefix.length);

  if (current.startsWith(pending)) {
    return 'back';
  }
  if (pending.startsWith(current)) {
    return 'next';
  }

  return null;
}

export function findWizardStructureNode(
  currentStep: number,
  currentPath: number[]
): WizardNode | null {
  let currentNode = WIZARD_STRUCTURE;
  for (let i = 0; i < currentStep; i += 1) {
    if (!currentNode.nodes) {
      return null;
    }
    const childNode = getWizardChildNode(currentNode, currentPath[i]);
    if (!childNode) {
      return null;
    }
    currentNode = childNode;
  }
  return currentNode;
}

export function getComponentClassAndProps(
  currentStep: number,
  currentPath: number[]
): WizardNode | null {
  return findWizardStructureNode(currentStep, currentPath);
}

export function validPathSelected(
  currentStep: number,
  currentPath: number[],
  values: WizardFormValues
) {
  const node = findWizardStructureNode(currentStep, currentPath);
  if (!node) return false;
  const { fieldName, map, noMapNeeded } = node;
  if (noMapNeeded) return true;
  if (!fieldName && currentStep === 3) return true;
  if (!fieldName) return false;
  const fieldValue = values[fieldName];
  const mapKey = coerceToString(fieldValue);
  return typeof (map && map[mapKey]) === 'number';
}
